import { PaginationMode } from "@hooks/table/use-table-pagination-types";

interface CalculateTotalCountParams {
  /** Pagination mode ("client" or "server") */
  mode: PaginationMode;

  /** Current number of loaded entities */
  entitiesLength: number;

  /** Total count from server (number or null if unknown) */
  totalCount?: number | null;

  /** Whether more items can be loaded from the server */
  hasMoreItems?: boolean;
}

/**
 * Calculate effective total count based on pagination mode and provided totals
 *
 * @returns Effective total count (-1 if unknown)
 */
export function calculateTotalCount({
  mode,
  entitiesLength,
  totalCount,
  hasMoreItems,
}: CalculateTotalCountParams): number {
  if (mode === "client") {
    return entitiesLength;
  }
  // If total count is known, use it
  if (totalCount !== null && totalCount !== undefined) {
    return totalCount;
  }
  // If we have unknown total but no more items to load, we know the exact total
  if (hasMoreItems === false) {
    return entitiesLength;
  }
  // Unknown total with more items available
  return -1;
}

interface CalculateLastPageParams {
  /** The effective total count of items (-1 if unknown) */
  effectiveTotalCount: number;

  /** Number of items per page */
  pageSize: number;

  /** Current number of loaded entities */
  entitiesLength: number;

  /** Whether more items can be loaded from the server */
  hasMoreItems?: boolean;
}

/**
 * Calculate the last page index (0-based)
 * Returns -1 if the last page cannot be determined
 *
 * @returns The last page index (0-based) or -1 if unknown
 */
export function calculateLastPage({
  effectiveTotalCount,
  pageSize,
  entitiesLength,
  hasMoreItems,
}: CalculateLastPageParams): number {
  if (effectiveTotalCount !== -1) {
    return Math.ceil(effectiveTotalCount / pageSize) - 1;
  }
  if (!hasMoreItems && entitiesLength > 0) {
    return Math.ceil(entitiesLength / pageSize) - 1;
  }
  return -1;
}

interface CalculateDisplayIndicesParams {
  /** Current page index (0-based) */
  pageIndex: number;

  /** Number of items per page */
  pageSize: number;

  /** Number of items on the current page */
  paginatedLength: number;
}

/**
 * Calculate display indices for the footer (1-based for display)
 *
 * @returns Object with 'from' and 'to' indices
 */
export function calculateDisplayIndices({
  pageIndex,
  pageSize,
  paginatedLength,
}: CalculateDisplayIndicesParams): { from: number; to: number } {
  const start = pageIndex * pageSize;
  return {
    from: paginatedLength > 0 ? start + 1 : 0,
    to: start + paginatedLength,
  };
}

interface GetPageChangeDirectionParams {
  /** The current page index (0-based) */
  currentPage: number;

  /** The new page index (0-based) */
  newPage: number;
}

/**
 * Determine the direction of page change
 *
 * @returns "next" if moving to the next page, "previous" if moving to the previous page, "jump" otherwise
 */
export function getPageChangeDirection({
  currentPage,
  newPage,
}: GetPageChangeDirectionParams): "next" | "previous" | "jump" {
  if (newPage === currentPage + 1) {
    return "next";
  }
  if (newPage === currentPage - 1) {
    return "previous";
  }
  return "jump";
}

interface ShouldDisableNextButtonParams {
  /** Current page index (0-based) */
  pageIndex: number;

  /** Last page index (0-based, -1 if unknown) */
  lastPage: number;

  /** Current number of loaded entities */
  entitiesLength: number;

  /** Number of items per page */
  pageSize: number;

  /** Whether more items can be loaded from the server */
  hasMoreItems?: boolean;
}

/**
 * Check if the next page button should be disabled
 *
 * @returns True if the next page button should be disabled, false otherwise
 */
export function shouldDisableNextButton({
  pageIndex,
  lastPage,
  entitiesLength,
  pageSize,
  hasMoreItems,
}: ShouldDisableNextButtonParams): boolean {
  if (lastPage !== -1) {
    return pageIndex >= lastPage;
  }
  // For unknown totals, disable if no more items
  return !hasMoreItems && pageIndex >= Math.floor(entitiesLength / pageSize);
}

interface LoadNextPageParams {
  /** The new page index to load */
  newPage: number;

  /** Number of items per page */
  pageSize: number;

  /** Current number of loaded entities */
  entitiesLength: number;

  /** Function to set loading state */
  setIsLoadingNextPage: (isLoading: boolean) => void;

  /** Total count of items, if known */
  totalCount?: number;

  /**
   * Callback to fetch the next page of data.
   *
   * **Note**: This function may throw errors during network requests.
   * The caller is responsible for error handling (e.g., wrapping calls in try-catch,
   * using error boundaries, or handling rejected promises appropriately).
   */
  fetchNextPage?: () => Promise<void>;
}

/**
 * Load next page of data if needed when navigating forward.
 *
 * @returns A promise that resolves when the fetch completes or immediately if no fetch is needed.
 * **Important**: If `fetchNextPage` throws an error, it will propagate through the returned promise.
 * Callers should handle errors appropriately (e.g., await with try-catch, or use .catch()).
 *
 * @example
 * ```ts
 * // Caller should handle errors
 * loadNextPage({...params})
 *   .catch(error => {
 *     console.error('Failed to load next page:', error);
 *     // Show error toast, etc.
 *   });
 * ```
 */
export function loadNextPage({
  newPage,
  pageSize,
  entitiesLength,
  setIsLoadingNextPage,
  totalCount,
  fetchNextPage,
}: LoadNextPageParams): Promise<void> {
  const requiredEnd = Math.min(
    (newPage + 1) * pageSize,
    totalCount ?? Infinity
  );

  const isFetchRequired = entitiesLength < requiredEnd;

  if (isFetchRequired && fetchNextPage) {
    setIsLoadingNextPage(true);
    return fetchNextPage().finally(() => {
      setIsLoadingNextPage(false);
    });
  }

  return Promise.resolve();
}
